import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, PanInfo, useAnimation } from 'framer-motion';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { MapPin, DollarSign, TrendingUp, Heart, X, Eye } from 'lucide-react';
import { Database } from '@/integrations/supabase/types';
import { getListingImage } from '@/utils/getListingImage'

type Listing = Database['public']['Views']['v_listings_with_image']['Row'] & { price: number | null };

interface SwipeableCardProps {
  listing: Listing;
  onSwipeLeft: (listing: Listing) => void;
  onSwipeRight: (listing: Listing) => void;
}

export function SwipeableCard({ listing, onSwipeLeft, onSwipeRight }: SwipeableCardProps) {
  const [exitX, setExitX] = useState(0);
  const [isDragging, setIsDragging] = useState(false);
  const controls = useAnimation();
  const navigate = useNavigate();

  const formatPrice = (price: number | null) => {
    if (!price) return 'Price on Application';
    return `AUD $${price.toLocaleString()}`;
  };

  const truncateText = (text: string, length: number = 120) => {
    if (text.length <= length) return text;
    return text.substring(0, length) + '...';
  };

  const handleDragEnd = (event: any, info: PanInfo) => {
    setIsDragging(false);
    const threshold = 100;
    
    if (info.offset.x > threshold) {
      // Swipe right - add to favorites
      setExitX(1000);
      onSwipeRight(listing);
    } else if (info.offset.x < -threshold) {
      // Swipe left - pass
      setExitX(-1000);
      onSwipeLeft(listing);
    } else {
      // Return to center
      controls.start({ x: 0, rotate: 0 });
    }
  };

  const handleDragStart = () => {
    setIsDragging(true);
  };

  return (
    <motion.div
      className="absolute inset-0 cursor-grab active:cursor-grabbing"
      drag="x"
      dragConstraints={{ left: 0, right: 0 }}
      onDragStart={handleDragStart}
      onDragEnd={handleDragEnd}
      animate={controls}
      exit={{ x: exitX, opacity: 0 }}
      transition={{ type: "spring", stiffness: 300, damping: 30 }}
      whileDrag={{ 
        scale: 1.05
      }}
      style={{ 
        rotate: isDragging ? "var(--rotation)" : 0 
      } as any}
    >
      <Card className="h-full overflow-hidden shadow-2xl relative">
        {/* Swipe Indicators */}
        <motion.div 
          className="absolute inset-0 bg-gradient-to-r from-green-500 to-green-400 z-10 flex items-center justify-start pl-8"
          initial={{ opacity: 0 }}
          animate={{ 
            opacity: isDragging ? (exitX > 0 ? 0.8 : 0) : 0 
          }}
        >
          <div className="flex items-center gap-2 text-white">
            <Heart className="h-8 w-8 fill-current" />
            <span className="text-2xl font-bold">LIKE</span>
          </div>
        </motion.div>

        <motion.div 
          className="absolute inset-0 bg-gradient-to-l from-red-500 to-red-400 z-10 flex items-center justify-end pr-8"
          initial={{ opacity: 0 }}
          animate={{ 
            opacity: isDragging ? (exitX < 0 ? 0.8 : 0) : 0 
          }}
        >
          <div className="flex items-center gap-2 text-white">
            <span className="text-2xl font-bold">PASS</span>
            <X className="h-8 w-8" />
          </div>
        </motion.div>

        {/* Image */}
        <div className="aspect-video overflow-hidden bg-muted">
          <img
            src={getListingImage(listing as any)}
            alt={listing.title ?? 'Business image'}
            className="w-full h-full object-cover"
            loading="lazy"
          />
        </div>

        <CardHeader className="pb-3">
          <div className="flex items-start justify-between gap-2 mb-2">
            <Badge variant="secondary" className="text-xs">
              {listing.sector}
            </Badge>
            <div className="text-right">
              <p className="text-lg font-bold text-primary">
                {formatPrice(listing.price)}
              </p>
            </div>
          </div>
          <CardTitle className="text-xl line-clamp-2">
            {listing.title}
          </CardTitle>
          {listing.teaser && (
            <CardDescription className="text-sm line-clamp-3">
              {truncateText(listing.teaser, 150)}
            </CardDescription>
          )}
        </CardHeader>

        <CardContent className="pt-0">
          <div className="space-y-3">
            {listing.location_suburb && listing.location_state && (
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <MapPin className="h-4 w-4 flex-shrink-0" />
                <span className="truncate">{listing.location_suburb}, {listing.location_state}</span>
              </div>
            )}
            
            <div className="grid grid-cols-2 gap-3 text-sm">
              {listing.revenue && (
                <div className="flex items-center gap-2 text-muted-foreground">
                  <DollarSign className="h-4 w-4" />
                  <span>Revenue: AUD ${(listing.revenue / 1000)}k</span>
                </div>
              )}
              {listing.ebitda && (
                <div className="flex items-center gap-2 text-muted-foreground">
                  <TrendingUp className="h-4 w-4" />
                  <span>EBITDA: AUD ${(listing.ebitda / 1000)}k</span>
                </div>
              )}
            </div>
          </div>
        </CardContent>

        {/* View Details Button and Swipe Instructions */}
        <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 flex flex-col items-center gap-2 z-20 pointer-events-none">
          <Button
            size="sm"
            variant="secondary"
            onClick={(e) => {
              e.stopPropagation();
              e.preventDefault();
              navigate(`/business/${listing.id}`, { state: { fromSwipe: true } });
            }}
            onMouseDown={(e) => {
              e.stopPropagation();
            }}
            onTouchStart={(e) => {
              e.stopPropagation();
            }}
            className="shadow-lg pointer-events-auto"
          >
            <Eye className="h-4 w-4 mr-2" />
            View Details
          </Button>
          <div className="bg-black/70 text-white text-xs px-3 py-1 rounded-full pointer-events-auto">
            Swipe ← to pass • Swipe → to like
          </div>
        </div>
      </Card>
    </motion.div>
  );
}